import { type DominoTile as Tile } from '@spin/shared';

/** Pip positions on a 3 × 3 grid (0–2 per axis) for a half standing upright; a lying half swaps the axes. */
const PIPS: Record<number, [number, number][]> = {
  0: [],
  1: [[1, 1]],
  2: [
    [0, 0],
    [2, 2],
  ],
  3: [
    [0, 0],
    [1, 1],
    [2, 2],
  ],
  4: [
    [0, 0],
    [2, 0],
    [0, 2],
    [2, 2],
  ],
  5: [
    [0, 0],
    [2, 0],
    [1, 1],
    [0, 2],
    [2, 2],
  ],
  6: [
    [0, 0],
    [0, 1],
    [0, 2],
    [2, 0],
    [2, 1],
    [2, 2],
  ],
};

/** Each number has its own pip colour, so matching ends are easy to spot. */
const COLOR = ['#000', '#e11d74', '#7c3aed', '#0891b2', '#16a34a', '#ea7a0c', '#2563eb'];

function Half({ value, x, y, lying }: { value: number; x: number; y: number; lying: boolean }) {
  return (
    <g>
      {PIPS[value]!.map(([col, row], i) => {
        const [cx, cy] = lying ? [row, col] : [col, row];
        return <circle key={i} cx={x + 8 + cx * 12} cy={y + 8 + cy * 12} r={4.2} fill={COLOR[value]} />;
      })}
    </g>
  );
}

/**
 * One domino. Upright tiles show `a` on top; lying tiles show `a` on the left (the chain is read left to right).
 * `face="down"` is the back of a tile, for the other players' hands.
 */
export function DominoTile({
  tile,
  lying = false,
  size = 40,
  face = 'up',
  className,
}: {
  tile?: Tile;
  lying?: boolean;
  /** Width of an upright tile in px (a lying tile is this tall). */
  size?: number;
  face?: 'up' | 'down';
  className?: string;
}) {
  const [w, h] = lying ? [80, 40] : [40, 80];
  const style = lying ? { width: size * 2, height: size } : { width: size, height: size * 2 };
  return (
    <svg
      className={`domino ${face === 'down' ? 'down' : ''} ${className ?? ''}`}
      viewBox={`0 0 ${w} ${h}`}
      style={style}
      role="img"
      aria-label={tile && face === 'up' ? `${tile[0]}-${tile[1]}` : 'tile'}
    >
      <rect x="1" y="1" width={w - 2} height={h - 2} rx="7" className="domino-body" />
      {face === 'up' && tile && (
        <>
          {lying ? (
            <line x1="40" y1="6" x2="40" y2="34" className="domino-line" />
          ) : (
            <line x1="6" y1="40" x2="34" y2="40" className="domino-line" />
          )}
          <Half value={tile[0]} x={0} y={0} lying={lying} />
          <Half value={tile[1]} x={lying ? 40 : 0} y={lying ? 0 : 40} lying={lying} />
        </>
      )}
    </svg>
  );
}
